import { useState } from 'react';
import {
  useMyStudentProfile,
  useMyTutorProfile,
  useUpdateMe,
  useUpdateMyStudentProfile,
  useUpdateMyTutorProfile,
} from '../../api/me';
import { useToast } from '../../components/Toast';
import { Spinner } from '../../components/Spinner';
import { Badge } from '../../components/Badge';
import { ApiError } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import type { UserProfile } from '../../types/api';

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="panel p-4">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-ink-3">{description}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

export function ProfileTab({ user }: { user: UserProfile }) {
  const { notify } = useToast();
  const updateMe = useUpdateMe();
  const isTutor = user.role === 'tutor';
  const isStudent = user.role === 'student';

  const tutorProfile = useMyTutorProfile(isTutor);
  const studentProfile = useMyStudentProfile(isStudent);
  const updateTutor = useUpdateMyTutorProfile();
  const updateStudent = useUpdateMyStudentProfile();

  const [fullName, setFullName] = useState(user.fullName);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [timezone, setTimezone] = useState(user.timezone);

  const saveAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Record<string, string> = {};
    if (fullName !== user.fullName) patch.fullName = fullName.trim();
    if (phone !== (user.phone ?? '')) patch.phone = phone.trim();
    if (timezone !== user.timezone) patch.timezone = timezone.trim();
    if (Object.keys(patch).length === 0) {
      notify('Nothing to save');
      return;
    }
    try {
      await updateMe.mutateAsync(patch);
      notify('Saved on their behalf', 'success');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not save.', 'error');
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section
        title="Account"
        description="What they see on their own settings page."
      >
        <form onSubmit={saveAccount} className="space-y-4">
          <label className="block">
            <span className="label">Full name</span>
            <input
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="label">Phone</span>
              <input
                className="input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+20 123 456 7890"
              />
            </label>
            <label className="block">
              <span className="label">Timezone</span>
              <input
                className="input"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              />
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" className="btn btn-primary" disabled={updateMe.isPending}>
              {updateMe.isPending ? 'Saving…' : 'Save'}
            </button>
            <span className="text-[0.6875rem] text-ink-3">
              Their password cannot be changed from here.
            </span>
          </div>
        </form>
      </Section>

      {isTutor && (
        <Section title="Tutor profile" description="Their public listing.">
          {tutorProfile.isLoading ? (
            <Spinner />
          ) : tutorProfile.data ? (
            <TutorProfileForm
              initial={tutorProfile.data}
              pending={updateTutor.isPending}
              onSave={async (patch) => {
                try {
                  await updateTutor.mutateAsync(patch);
                  notify('Tutor profile saved', 'success');
                } catch (err) {
                  notify(err instanceof ApiError ? err.message : 'Could not save.', 'error');
                }
              }}
            />
          ) : (
            <p className="text-xs text-ink-3">No tutor profile found.</p>
          )}
        </Section>
      )}

      {isStudent && (
        <Section title="Student profile" description="Grade, school, and guardian contact.">
          {studentProfile.isLoading ? (
            <Spinner />
          ) : studentProfile.data ? (
            <StudentProfileForm
              initial={studentProfile.data}
              pending={updateStudent.isPending}
              onSave={async (patch) => {
                try {
                  await updateStudent.mutateAsync(patch);
                  notify('Student profile saved', 'success');
                } catch (err) {
                  notify(err instanceof ApiError ? err.message : 'Could not save.', 'error');
                }
              }}
            />
          ) : (
            <p className="text-xs text-ink-3">No student profile found.</p>
          )}
        </Section>
      )}
    </div>
  );
}

function TutorProfileForm({
  initial,
  pending,
  onSave,
}: {
  initial: { headline?: string; bio?: string; subjects: string[]; hourlyRateCents: number; currency: string; yearsExperience?: number; isVerified: boolean; ratingAvg: number; ratingCount: number };
  pending: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [headline, setHeadline] = useState(initial.headline ?? '');
  const [bio, setBio] = useState(initial.bio ?? '');
  const [subjects, setSubjects] = useState(initial.subjects.join(', '));
  const [rate, setRate] = useState(String(initial.hourlyRateCents / 100));
  const [years, setYears] = useState(
    initial.yearsExperience != null ? String(initial.yearsExperience) : '',
  );

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          headline: headline.trim() || undefined,
          bio: bio.trim() || undefined,
          subjects: subjects
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          hourlyRateCents: Math.round(Number(rate) * 100),
          yearsExperience: years ? Number(years) : undefined,
        });
      }}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
        {initial.isVerified ? <Badge tone="good">Verified</Badge> : <Badge>Not verified</Badge>}
        <span>
          {initial.ratingCount > 0
            ? `${initial.ratingAvg.toFixed(2)} from ${initial.ratingCount} ratings`
            : 'Not rated yet'}
        </span>
        <span>· {formatMoney(initial.hourlyRateCents, initial.currency)}/hour</span>
      </div>

      <label className="block">
        <span className="label">Headline</span>
        <input
          className="input"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="IGCSE Maths & Physics"
        />
      </label>

      <label className="block">
        <span className="label">Subjects</span>
        <input
          className="input"
          value={subjects}
          onChange={(e) => setSubjects(e.target.value)}
          placeholder="algebra, physics"
        />
        <span className="mt-1.5 block text-[0.6875rem] text-ink-3">
          Comma separated. These are what students filter by.
        </span>
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="label">Hourly rate</span>
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Years of experience</span>
          <input
            className="input"
            type="number"
            min="0"
            value={years}
            onChange={(e) => setYears(e.target.value)}
          />
        </label>
      </div>

      <label className="block">
        <span className="label">Bio</span>
        <textarea
          className="textarea"
          rows={4}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
        />
      </label>

      <button type="submit" className="btn btn-primary" disabled={pending}>
        {pending ? 'Saving…' : 'Save tutor profile'}
      </button>
    </form>
  );
}

function StudentProfileForm({
  initial,
  pending,
  onSave,
}: {
  initial: {
    gradeLevel?: string;
    schoolName?: string;
    guardianEmail?: string;
    guardianPhone?: string;
  };
  pending: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [gradeLevel, setGradeLevel] = useState(initial.gradeLevel ?? '');
  const [schoolName, setSchoolName] = useState(initial.schoolName ?? '');
  const [guardianEmail, setGuardianEmail] = useState(initial.guardianEmail ?? '');
  const [guardianPhone, setGuardianPhone] = useState(initial.guardianPhone ?? '');

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          gradeLevel: gradeLevel.trim() || undefined,
          schoolName: schoolName.trim() || undefined,
          guardianEmail: guardianEmail.trim() || undefined,
          guardianPhone: guardianPhone.trim() || undefined,
        });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="label">Grade</span>
          <input
            className="input"
            value={gradeLevel}
            onChange={(e) => setGradeLevel(e.target.value)}
            placeholder="Grade 10"
          />
        </label>
        <label className="block">
          <span className="label">School</span>
          <input
            className="input"
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Guardian email</span>
          <input
            className="input"
            type="email"
            value={guardianEmail}
            onChange={(e) => setGuardianEmail(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Guardian phone</span>
          <input
            className="input"
            value={guardianPhone}
            onChange={(e) => setGuardianPhone(e.target.value)}
          />
        </label>
      </div>

      <button type="submit" className="btn btn-primary" disabled={pending}>
        {pending ? 'Saving…' : 'Save student profile'}
      </button>
    </form>
  );
}
